import moment, { Moment } from 'moment-timezone';
import * as React from 'react';

import Button from 'components/ButtonDeprecated';
import Icon from 'components/Icon';
import Label from 'components/Label';
import LayerTrigger from 'components/LayerTrigger';
import Hovercard from 'components/Hovercard';
import HovercardBody from 'components/HovercardBody';
import DatePickerInput from 'components/DatePickerInput';
import TimePickerInput from 'components/TimePickerInput';
// @ts-ignore
import { getNext30MinuteInterval, getDefaultScheduledTime } from 'lib/SchedulingMessage';

import TooltipHovercardIconButton from './TooltipHovercardIconButton';
import './styles/VisualComposerScheduler.scss';

const isToday = (date: Date | Moment) =>
  (moment.isMoment(date) ? date : moment(date)).isSame(moment(), 'day');

export type Props = {
  scheduledTime?: Date;
  disabled?: boolean;
  onSetScheduledTime: (inputDate?: Date | null) => void;
};

type State = { selectedTime: Date };

const combineDateAndTime = (date: Date, time: Date) => {
  const result = new Date(date);
  result.setHours(time.getHours());
  result.setMinutes(time.getMinutes());
  return result;
};

/* Contains a date input, a time input, and a delete button which clears all scheduling state.
 * When scheduling is off (ie, scheduledTime prop is falsy), the date input shows the default time
 * from getDefaultScheduledTime, while the time input is blank.
 *
 * In order to enable scheduling, the user must select a time from the time input. Manipulating the
 * date input while scheduling is off will only update selectedTime in local state, which is reset
 * when the hovercard is reopened.
 *
 * We accomplish this by tracking two Date values:
 * - scheduledTime comes from props; if it has a value, scheduling is on, else scheduling is off
 * - selectedTime is internal state, and gets fed back to scheduledTime via the onSetScheduledTime
 *   callback under one of two conditions:
 *   a) the update is being triggered from the time input
 *   b) scheduledTime is already set (hence update on every change)
 */
export default class VisualComposerScheduler extends React.Component<Props, State> {
  state: State = { selectedTime: getDefaultScheduledTime() };

  setSelectedTime = (newTime?: Date) => {
    const selectedTime = newTime ?? this.props.scheduledTime ?? getDefaultScheduledTime();
    this.setState({ selectedTime });
  };

  updateSelectedTime = ({ date, time }: { date?: Date; time?: Date }) => {
    const { selectedTime } = this.state;
    const newTime = combineDateAndTime(date ?? selectedTime, time ?? selectedTime);
    this.setSelectedTime(newTime);

    if (time ?? this.props.scheduledTime) {
      this.props.onSetScheduledTime(newTime);
    }
  };

  renderLayer = (layerTrigger: LayerTrigger) => {
    const { scheduledTime } = this.props;
    const { selectedTime } = this.state;
    const pickerProps = {
      className: 'VisualComposerScheduler-input',
      placement: 'top' as 'top',
      size: 'small',
    };
    const isDateInPast = !!selectedTime && new Date() > selectedTime;

    return (
      <Hovercard className="VisualComposerScheduler-hovercard">
        <HovercardBody className="VisualComposerScheduler-hovercard-body">
          <div>
            <Label>Date</Label>
            <DatePickerInput
              data-test="date-picker"
              onChange={(date?: Date) => this.updateSelectedTime({ date })}
              value={selectedTime}
              min={getNext30MinuteInterval()}
              // @ts-ignore - react-widgets' types are incorrect, format does support functions
              format={(date?: Date) => {
                const m = moment(date);
                if (isToday(m)) return 'Today';
                if (m.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow';
                return m.format('M/D/YYYY');
              }}
              {...pickerProps}
            />
          </div>
          <div>
            <Label>Time</Label>
            <TimePickerInput
              data-test="time-picker"
              data-test-selector="time-picker-input"
              error={isDateInPast}
              onChange={(time?: Date) => this.updateSelectedTime({ time })}
              value={scheduledTime}
              /* Until `scheduledTime` is set, this component's value will be null and react-widgets will treat
               * it as the current time, even if `selectedTime` is set to a future date using the date input.
               *
               * Setting `min` to `getNext30MinuteInterval` disallows times before the current time, which is
               * correct only if `selectedTime` is set to the current date. Since the time picker doesn't know
               * whether that's the case, we have to explicitly pass `undefined` to `min` if `selectedTime` is
               * set to a future date.
               */
              min={isToday(selectedTime) ? getNext30MinuteInterval() : undefined}
              placeholder="Select"
              {...pickerProps}
            />
          </div>
          <Button
            level="icon"
            onClick={() => {
              this.props.onSetScheduledTime(null);
              layerTrigger.hideLayer();
            }}
            className="VisualComposerScheduler-deleteIconButton">
            <Icon name="trash" color="apple" />
          </Button>
        </HovercardBody>
      </Hovercard>
    );
  };

  render() {
    const { scheduledTime, disabled } = this.props;
    return (
      <TooltipHovercardIconButton
        controlled
        layerPlacement="top"
        layerAlignment="left"
        layer={this.renderLayer}
        onShow={this.setSelectedTime}
        switchedOn={!!scheduledTime}
        disabled={disabled}
        tooltipText={
          disabled
            ? 'Scheduling is currently only available for schools and classes.'
            : 'Schedule message'
        }
        className="VisualComposerScheduler"
        data-test="visual_composer_scheduler"
        data-test-selector="visual_composer_scheduler"
        icon="clock-stroked"
      />
    );
  }
}
